import React from 'react';
import { Box, Typography, Paper, List, ListItemButton, ListItemText, Divider, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

type DriveFile = {
  id: string;
  name: string;
  mimeType?: string;
  webViewLink?: string;
};

const MyDocsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [folderIdOverride, setFolderIdOverride] = React.useState<string | null>(null);
  const folderId = folderIdOverride || ((user as any)?.google_drive_user_folder_id || null);

  const [files, setFiles] = React.useState<DriveFile[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState<boolean>(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    // Jeśli nie mamy folderId w kontekście, spróbuj dociągnąć najświeższe /auth/me
    (async () => {
      if (!folderIdOverride && !(user as any)?.google_drive_user_folder_id) {
        try {
          const me = await api.get('/auth/me', { suppressAuthErrorLog: true } as any);
          const fid = me?.data?.data?.google_drive_user_folder_id || null;
          if (fid && !cancelled) setFolderIdOverride(fid);
        } catch {}
      }
    })();
    const load = async () => {
      if (!folderId) {
        setFiles([]);
        setSelectedId(null);
        return;
      }
      setLoading(true);
      try {
        const res = await api.get('/drive/files', { params: { folderId, pageSize: 200 } });
        const list: DriveFile[] = (res?.data?.data || []).map((f: any) => ({
          id: f.id,
          name: f.name,
          mimeType: f.mimeType,
          webViewLink: f.webViewLink
        }));
        if (!cancelled) {
          setFiles(list);
          setSelectedId(list.length ? list[0].id : null);
        }
      } catch {
        if (!cancelled) {
          setFiles([]);
          setSelectedId(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [folderId]);

  const selectedFile = React.useMemo(() => files.find((f) => f.id === selectedId) || null, [files, selectedId]);

  // Pobierz zawartość z autoryzacją i pokaż w podglądzie jako blob URL,
  // aby uniknąć problemu z brakiem nagłówka Authorization w <iframe>
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
        setPreviewUrl(null);
      }
      setPreviewError(null);
      if (!selectedFile) return;
      setPreviewLoading(true);
      try {
        const base = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
        const isPdf = String(selectedFile.mimeType || '').toLowerCase().includes('pdf');
        const url = isPdf
          ? `/drive/files/${selectedFile.id}/download`
          : `/drive/files/${selectedFile.id}/export?mimeType=application/pdf`;
        const resp = await api.get(url, { responseType: 'blob' as any });
        if (cancelled) return;
        const blob = resp.data as Blob;
        const objUrl = URL.createObjectURL(blob);
        setPreviewUrl(objUrl);
      } catch (e: any) {
        if (!cancelled) setPreviewError(e?.response?.data?.message || e?.message || 'Nie udało się pobrać pliku.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedFile]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
      {/* Wróć do strony głównej – w tej samej pozycji co na stronie projektu */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        aria-label="Wróć do strony głównej"
        role="button"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: '#1F1FB0'
          }}
        >
          Wróć do strony głównej
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Moje dokumenty
        </Typography>
      </Box>

      {!folderId ? (
        <Typography sx={{ color: '#6B7280' }}>
          Brak przypisanego folderu dokumentów użytkownika. Zaloguj się ponownie albo skontaktuj się z administratorem.
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 380px) 1fr', gap: 1.5 }}>
          {/* Lewy panel: lista plików */}
          <Paper elevation={0} sx={{ border: '1px solid #EDEDED', borderRadius: '8px', p: 0, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: 14, color: '#595966', fontWeight: 500 }}>Pliki w moim folderze</Typography>
            </Box>
            <Divider />
            <Box sx={{ height: 'calc(74vh)', overflowY: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <List dense disablePadding>
                  {(files || []).map((f) => (
                    <ListItemButton
                      key={f.id}
                      selected={selectedId === f.id}
                      onClick={() => setSelectedId(f.id)}
                      sx={{ px: 2, py: 1 }}
                    >
                      <ListItemText
                        primary={f.name}
                        secondary={f.mimeType || undefined}
                        primaryTypographyProps={{ sx: { fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                        secondaryTypographyProps={{ sx: { fontSize: 12, color: '#6B7280' } }}
                      />
                    </ListItemButton>
                  ))}
                  {(files || []).length === 0 && (
                    <Box sx={{ px: 2, py: 2 }}>
                      <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Folder jest pusty.</Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          </Paper>

          {/* Prawy panel: podgląd */}
          <Paper elevation={0} sx={{ border: '1px solid #EDEDED', borderRadius: '8px', p: 0, overflow: 'hidden', minHeight: '74vh' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #EDEDED' }}>
              <Typography sx={{ fontSize: 14, color: '#595966', fontWeight: 500 }}>
                Podgląd
              </Typography>
              {selectedFile && (
                <Typography sx={{ fontSize: 13, color: '#2D2D32', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFile.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ height: 'calc(74vh - 52px)', bgcolor: '#fff', display: 'grid', placeItems: 'center' }}>
              {previewLoading ? (
                <CircularProgress size={22} />
              ) : previewError ? (
                <Typography sx={{ color: '#B00020', px: 2, textAlign: 'center' }}>{previewError}</Typography>
              ) : previewUrl ? (
                <iframe
                  title="Podgląd pliku"
                  src={previewUrl}
                  style={{ border: 0, width: '100%', height: '100%' }}
                  allow="autoplay"
                />
              ) : (
                <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                  <Typography sx={{ color: '#6B7280' }}>Wybierz plik z listy, aby zobaczyć podgląd.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MyDocsPage;
